import { ErrorMessage, TaInput } from '@components/shared';
import { NotificationSnackbar } from '@components/shared/notificationSnackbar';
import { TaModal } from '@components/shared/TaModal/TaModal';
import { FarmerUserCard } from '@features/dashboard/farmerDashboard/farmerUserCard/farmerUserCard';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateUser } from 'services/users';
import * as Yup from 'yup';

interface UserDetailsProps {
	firstName: string;
	lastName: string;
	readonly?: boolean;
}

const nameValidationSchema = Yup.object({
	firstName: Yup.string().required('fieldNameError'),
	lastName: Yup.string().required('fieldNameError'),
});

const initialValues = {
	firstName: '',
	lastName: '',
};

export const FarmerUserCardWrapper = ({
	firstName,
	lastName,
	readonly = false,
}: UserDetailsProps) => {
	const { t } = useTranslation();
	const mutate = useUpdateUser();
	const [open, setOpen] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const formik = useFormik({
		initialValues: initialValues,
		validationSchema: nameValidationSchema,
		onSubmit: async (values) => {
			setIsLoading(true);
			try {
				await mutate({
					first_name: values.firstName,
					last_name: values.lastName,
				});
				setOpen(false);
				setSnackbarOpen(true);
				setIsLoading(false);
			} catch (error) {
				console.error('Update failed:', error);
				setIsLoading(false);
			}
		},
	});

	const { setValues } = formik;

	useEffect(() => {
		setValues({
			firstName: firstName,
			lastName: lastName,
		});
	}, [firstName, lastName, setValues]);

	return (
		<FarmerUserCard
			name={`${formik.values.firstName} ${formik.values.lastName}`}
			onEdit={() => setOpen(true)}
			readonly={readonly}
		>
			<TaModal
				save={formik.handleSubmit}
				open={open}
				onClose={() => setOpen(false)}
				saveButtonLoading={isLoading}
				disableSaveButton={isLoading}
				title={t('fullName')}
				showActions
			>
				<div className="flex flex-col gap-[10px] mt-[20px]">
					<span className="roboto-regular text-[16px] leading-[140%]">{t('myName')}</span>
					<div>
						<TaInput
							icon={<PersonOutlineOutlinedIcon />}
							id="firstName"
							label={t('firstName')}
							size="small"
							isRequired
							value={formik.values.firstName}
							onChange={formik.handleChange}
							error={formik.touched.firstName && Boolean(formik.errors.firstName)}
						/>
						{formik.touched.firstName && formik.errors.firstName && (
							<ErrorMessage
								message={t(formik.errors.firstName || '', {
									fieldName: t('firstName'),
								})}
							/>
						)}
					</div>
					<div>
						<TaInput
							icon={<PersonOutlineOutlinedIcon />}
							id="lastName"
							label={t('lastName')}
							size="small"
							value={formik.values.lastName}
							onChange={formik.handleChange}
							error={formik.touched.lastName && Boolean(formik.errors.lastName)}
						/>
						{formik.touched.lastName && formik.errors.lastName && (
							<ErrorMessage
								message={t(formik.errors.lastName || '', {
									fieldName: t('lastName'),
								})}
							/>
						)}
					</div>
				</div>
			</TaModal>
			<NotificationSnackbar
				open={snackbarOpen}
				onClose={() => setSnackbarOpen(false)}
				title={t('successfullyUpdated')}
				success
				description={t('profileUpdated', {
					title: t('fullName'),
				})}
			/>
		</FarmerUserCard>
	);
};
